import {
  computed,
  effect,
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from "@angular/core";
import { Observable, of, tap } from "rxjs";
import {
  CreateVisitDto,
  FilterVisitDto,
  ResponseVisitDto,
  UpdateVisitDto,
} from "../../../api/models";
import { VisitsApiService } from "../../api/healthcare/visits-api.service";
import { CachedServiceBase } from "../../base/cached-service.base";
// import { mockVisits } from "../../../helpers/mocks/visits.mocks";

@Injectable({
  providedIn: "root",
})
export class CachedVisitsService extends CachedServiceBase<ResponseVisitDto> {
  private readonly _visitsApiService = inject(VisitsApiService);

  // =============================================================================
  // DB FILTERS (trigger backend calls)
  // =============================================================================
  /** Patient ID filter - triggers DB call when changed */
  private readonly _patientId: WritableSignal<string | null> = signal(null);

  /** Doctor ID filter - triggers DB call when changed */
  private readonly _doctorId: WritableSignal<string | null> = signal(null);

  /** Status filter - triggers DB call when changed */
  private readonly _status: WritableSignal<string | undefined> = signal(undefined);

  /** Phase ID filter - triggers DB call when changed */
  private readonly _phaseId: WritableSignal<string | null> = signal(null);

  // =============================================================================
  // CLIENT-SIDE FILTERS (live filtering)
  // =============================================================================
  /** Title filter for live search */
  public readonly titleFilter: WritableSignal<string> = signal("");

  /** Code filter for live search */
  public readonly codeFilter: WritableSignal<string> = signal("");

  /** First name filter for live search */
  public readonly firstNameFilter: WritableSignal<string> = signal("");

  /** Last name filter for live search */
  public readonly lastNameFilter: WritableSignal<string> = signal("");

  // =============================================================================
  // CONSTRUCTOR & EFFECTS
  // =============================================================================
  constructor() {
    super();

    // Effect: reset pagination when client filters change
    effect(() => {
      this.titleFilter();
      this.codeFilter();
      this.firstNameFilter();
      this.lastNameFilter();
      // Reset to first page when client filters change
      this.resetPagination();
    });
  }

  // =============================================================================
  // IMPLEMENT ABSTRACT METHODS
  // =============================================================================

  /**
   * Apply client-side filters to backend data
   */
  protected getFilteredData(): Signal<ResponseVisitDto[]> {
    return computed(() => {
      const allData = this.data();
      const title = this.titleFilter().toLowerCase().trim();
      const code = this.codeFilter().toLowerCase().trim();
      const firstName = this.firstNameFilter().toLowerCase().trim();
      const lastName = this.lastNameFilter().toLowerCase().trim();

      if (!title && !code && !firstName && !lastName) return allData;

      return allData.filter((item) => {
        const matchesTitle =
          !title || item.title?.toLowerCase().includes(title);
        const matchesCode = !code || item.code?.toLowerCase().includes(code);
        const matchesFirstName =
          !firstName || item.patientFirstName.toLowerCase().includes(firstName);
        const matchesLastName =
          !lastName || item.patientLastName.toLowerCase().includes(lastName);

        return (
          matchesTitle && matchesCode && matchesFirstName && matchesLastName
        );
      });
    });
  }

  public refresh(): void {
    const patientId = this._patientId();
    const doctorId = this._doctorId();
    const status = this._status();
    const phaseId = this._phaseId();
    if ((patientId || doctorId) && status) {
      this._fetchFromBackend(patientId, doctorId, status, phaseId);
    }
  }

  public reset(): void {
    this._patientId.set(null);
    this._doctorId.set(null);
    this._status.set(undefined);
    this._phaseId.set(null);
    this.titleFilter.set("");
    this.codeFilter.set("");
    this.firstNameFilter.set("");
    this.lastNameFilter.set("");
    this.clearData();
  }

  // =============================================================================
  // COMPUTED SIGNALS
  // =============================================================================

  /** Signal to track if there are active text filters */
  public readonly hasActiveFilters: Signal<boolean> = computed(() => {
    const title = this.titleFilter().trim();
    const code = this.codeFilter().trim();
    const firstName = this.firstNameFilter().trim();
    const lastName = this.lastNameFilter().trim();
    return !!(title || code || firstName || lastName);
  });

  // =============================================================================
  // PRIVATE METHODS
  // =============================================================================

  /**
   * Fetch data from backend based on patientId/doctorId, status and phaseId
   */
  private _fetchFromBackend(
    patientId: string | null,
    doctorId: string | null,
    status: string | undefined,
    phaseId: string | null,
  ): void {
    const filterData: Partial<FilterVisitDto> = { status };
    if (patientId) filterData.patientId = patientId;
    if (doctorId) filterData.doctorId = doctorId;
    if (phaseId) filterData.phaseId = phaseId;

    this._visitsApiService
      .filter$(filterData)
      .pipe(
        tap((allResults) => {
          // Apply client-side filtering and deduplication
          const processedResults = this._processVisitResults(allResults, status);
          this.updateData(processedResults);
        })
      )
      .subscribe();
  }

  /**
   * Process visit results: filter by status and deduplicate.
   * This is necessary due to a backend limitation where phaseId filtering
   * doesn't respect status filters.
   * @param results Raw results from backend
   * @param status Optional status filter (can contain multiple statuses separated by /)
   * @returns Processed and deduplicated results
   */
  private _processVisitResults(
    results: ResponseVisitDto[],
    status: string | undefined
  ): ResponseVisitDto[] {
    // Step 1: Filter by status if needed
    // Backend bug workaround: when phaseId is used, backend returns all visits regardless of status
    let filtered = results;
    if (status && this._phaseId()) {
      const allowedStatuses = new Set(
        status.split('/').map(s => s.trim().toLowerCase())
      );
      filtered = results.filter(visit => 
        allowedStatuses.has(visit.status?.toLowerCase() || '')
      );
    }
    
    // Step 2: Deduplicate by ID (in case backend returns duplicates)
    const uniqueMap = new Map<string, ResponseVisitDto>();
    filtered.forEach(visit => {
      if (visit.id && !uniqueMap.has(visit.id)) {
        uniqueMap.set(visit.id, visit);
      }
    });
    
    return Array.from(uniqueMap.values());
  }

  // =============================================================================
  // PUBLIC API
  // =============================================================================

  /**
   * Set patient ID (effect will trigger backend call)
   */
  public setPatientId(patientId: string): void {
    this._patientId.set(patientId);
    this.refresh();
  }

  /**
   * Set doctor ID (effect will trigger backend call)
   */
  public setDoctorId(doctorId: string): void {
    this._doctorId.set(doctorId);
    this.refresh();
  }

  /**
   * Set status (effect will trigger backend call)
   */
  public setStatus(status: string): void {
    this._status.set(status);
    this.refresh();
  }

  /**
   * Set phase ID (triggers backend call)
   */
  public setPhaseId(phaseId: string | null): void {
    this._phaseId.set(phaseId);
    this.refresh();
  }

  /**
   * Initializes the filters all at once with the provided payload to avoid the retrigger of the backend call.
   * @param payload the payload containing patientId, doctorId, status, and optionally phaseId.
   */
  public initializeFilters(payload: { patientId: string | null; doctorId: string | null; status: string; phaseId?: string | null }): void {
    this._patientId.set(payload.patientId);
    this._doctorId.set(payload.doctorId);
    this._status.set(payload.status);
    if (payload.phaseId !== undefined) {
      this._phaseId.set(payload.phaseId);
    }
    this.refresh();
  }

  /**
   * Clear text filters (title and code)
   */
  public clearTextFilters(): void {
    this.titleFilter.set("");
    this.codeFilter.set("");
    this.firstNameFilter.set("");
    this.lastNameFilter.set("");
  }

  /**
   * Legacy compatibility - using base class pagination
   */
  public nextPage(): void {
    this.goToNextPage();
  }

  /**
   * Legacy compatibility - using base class pagination
   */
  public previousPage(): void {
    this.goToPreviousPage();
  }

  /**
   * Legacy compatibility - get current page index
   */
  public currentPageIndex(): number {
    return this.paginationInfo().currentPage;
  }

  /**
   * Legacy compatibility - has next page
   */
  public hasNextPage(): boolean {
    return this.paginationInfo().hasNextPage;
  }

  /**
   * Legacy compatibility - has previous page
   */
  public hasPreviousPage(): boolean {
    return this.paginationInfo().hasPreviousPage;
  }

  /**
   * Legacy compatibility - should show pagination
   */
  public shouldShowPagination(): boolean {
    return this.paginationInfo().shouldShowPagination;
  }

  /**
   * Legacy compatibility - get total pages
   */
  public totalPages(): number {
    return this.paginationInfo().totalPages;
  }

  // =============================================================================
  // CRUD OPERATIONS
  // =============================================================================

  /**
   * Creates a new visit
   * @param body The DTO containing visit creation data
   * @returns Observable of the created visit
   */
  public create$(body: CreateVisitDto): Observable<ResponseVisitDto> {
    return this._visitsApiService.create$(body).pipe(tap(() => this.refresh()));
  }

  /**
   * Updates an existing visit
   * @param id The id of the visit
   * @param body The DTO containing visit update data
   * @returns Observable of the updated visit
   */
  public update$(
    id: string,
    body: UpdateVisitDto,
  ): Observable<ResponseVisitDto> {
    return this._visitsApiService
      .update$(id, body)
      .pipe(tap(() => this.refresh()));
  }

  /**
   * Deletes a visit by id
   * @param id The id of the visit
   * @returns Observable of operation result
   */
  public delete$(id: string): Observable<void> {
    return this._visitsApiService
      .delete$(id)
      .pipe(tap(() => this.refresh()));
  }

  /**
   * Retrieves a single visit
   * @param id The id of the visit to retrieve
   * @returns Observable of the visit
   */
  public findOne$(id: string): Observable<ResponseVisitDto> {
    // Search in backend data cache
    const allData = this.data();
    const cached = allData.find((visit) => visit.id === id);

    if (cached) {
      return of(cached);
    }

    return this._visitsApiService.findOne$(id);
  }

  // =============================================================================
  // LEGACY COMPATIBILITY FOR EXISTING COMPONENTS
  // =============================================================================

  /**
   * Legacy compatibility - reset error (not needed with new architecture)
   */
  public resetError(): void {
    // No-op - error handling is centralized now
  }
}
